import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appMaxLength]',
  standalone: false
})
export class MaxLengthDirective {
  @Input('appMaxLength') maxLength: number = 0;

  constructor(private readonly el: ElementRef) {}

  /**
   * WAY 1: Keydown Event
   * Blocks typing once the maximum length is reached,
   * but still allows navigation (arrows), backspace, delete, copy/paste shortcuts.
   */
  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const input = this.el.nativeElement;

    // Allow navigation/editing keys, and control keys
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 
      'Home', 'End'
    ];

    if (allowedKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
      return; // Permit these keystrokes
    }

    const currentValue = input.value || '';
    const selectionLength = (input.selectionEnd || 0) - (input.selectionStart || 0);

    // If typing would exceed the maxLength (ignoring any selected text that will be replaced)
    if (currentValue.length - selectionLength >= this.maxLength) {
      event.preventDefault();
    }
  }

  /**
   * WAY 2: Input Event
   * Handles any browser autofills, virtual keyboards, etc.
   */
  @HostListener('input', ['$event'])
  onInputChange(event: Event): void {
    this.sanitize();
  }

  /**
   * WAY 3: Blur Event
   * Double-checks and sanitizes on focus loss.
   */
  @HostListener('blur', ['$event'])
  onBlur(event: Event): void {
    this.sanitize();
  }

  /**
   * WAY 4: Paste Event
   * Intercepts pasting to truncate pasted content that exceeds the remaining character budget.
   */
  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      const input = this.el.nativeElement;
      const currentValue = input.value || '';
      const selectionLength = (input.selectionEnd || 0) - (input.selectionStart || 0);

      const availableSpace = this.maxLength - (currentValue.length - selectionLength);

      if (availableSpace <= 0) {
        event.preventDefault();
      } else if (pastedText.length > availableSpace) {
        event.preventDefault();
        const truncatedPaste = pastedText.substring(0, availableSpace);
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        
        input.value = currentValue.substring(0, start) + truncatedPaste + currentValue.substring(end);
        this.sanitize();
      }
    }
  }

  /**
   * WAY 5: Drop Event
   * Prevents dropping text or truncates dropped text if it exceeds the limit.
   */
  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    const dataTransfer = event.dataTransfer;
    if (dataTransfer) {
      const droppedText = dataTransfer.getData('text');
      const input = this.el.nativeElement;
      const currentValue = input.value || '';
      const selectionLength = (input.selectionEnd || 0) - (input.selectionStart || 0);

      const availableSpace = this.maxLength - (currentValue.length - selectionLength);

      if (availableSpace <= 0) {
        event.preventDefault();
      } else if (droppedText.length > availableSpace) {
        event.preventDefault();
        const truncatedDrop = droppedText.substring(0, availableSpace);
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;

        input.value = currentValue.substring(0, start) + truncatedDrop + currentValue.substring(end);
        this.sanitize();
      }
    }
  }

  /**
   * Helper function to truncate input to max length and update Angular bindings
   */
  private sanitize(): void {
    const input = this.el.nativeElement;
    const value = input.value || '';

    if (value.length > this.maxLength) {
      input.value = value.substring(0, this.maxLength);
      
      // Dispatch input event to notify NgModel/FormControl
      const newEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(newEvent);
    }
  }
}
